import { Component, computed, effect, inject, input, OnDestroy, output, signal } from '@angular/core';

import { AgencyStore, AgencyUserGridRowViewModel, AgencyUserGridStore, LookupStore } from '@program-suite/data-common';
import { GridEmitter, GridOptions } from '@program-suite/ui-grid';
import { AuthStore } from '@program-suite/data-auth';
import { Sort } from '@angular/material/sort';
import { UiGridComponent } from '../../../../../shared/ui-grid/src/lib/ui-grid/ui-grid.component';
import {
  AddUsersModalComponent,
  ButtonComponent,
  ConfirmModalComponent,
  ConfirmModalData,
  EditUserModalComponent,
  ModalService,
  ModalType,
  SnackbarComponent,
  ToastType,
} from '@program-suite/ui';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'lib-feature-all-users',
  imports: [UiGridComponent, ButtonComponent],
  templateUrl: './feature-all-users.component.html',
  styleUrl: './feature-all-users.component.scss',
})
export class FeatureAllUsersComponent {
  agencyUserGridStore = inject(AgencyUserGridStore);
  lookupStore = inject(LookupStore);
  authStore = inject(AuthStore);
  agencyStore = inject(AgencyStore);

  defaultSortColumn = 'firstName';
  defaultSortDirection = 'ASCENDING';
  totalNumberOfItems = computed<number>(() =>
    this.agencyUserGridStore.resource.value() ? this.agencyUserGridStore.resource.value()?.totalCount ?? 0 : 0,
  );
  data = computed<AgencyUserGridRowViewModel[]>(() =>
    this.agencyUserGridStore.resource.value() ? this.agencyUserGridStore.resource.value()?.rows ?? [] : [],
  );
  gridOptions = signal<GridOptions>({
    filterData: [
      { name: 'From', formControlName: 'dateFrom', type: 'date', dateOffset: -365 },
      { name: 'To', formControlName: 'dateTo', type: 'date', dateOffset: 0 },
      { name: 'Search', formControlName: 'searchTerm', type: 'search' },
      { name: 'Agency', formControlName: 'agencyId', type: 'dropdown', lookup: this.lookupStore.agencies },
      { name: 'Tenant', formControlName: 'tenantId', type: 'dropdown', lookup: this.lookupStore.tenants, hideFilter: !this.authStore.isAdmin() },
      { name: 'Status', formControlName: 'status', type: 'dropdown', lookup: this.lookupStore.agencyUserStatus },
      { name: 'Role', formControlName: 'role', type: 'dropdown', lookup: this.lookupStore.agencyRoles, hideFilter: this.authStore.isAdmin() },
    ],
    filterValues: signal({}),
    openFilters: false,
    columns: [
      { header: 'First Name', controlName: 'firstName' },
      { header: 'Last Name', controlName: 'lastName' },
      { header: 'Email Address', controlName: 'email' },
      { header: 'Agency Name', controlName: 'agencyName' },
      { header: 'Role', controlName: 'role', hideColumn: this.authStore.isAdmin() },
      { header: 'Tenant', controlName: 'tenantName', hideColumn: !this.authStore.isAdmin() },
      { header: 'Status', controlName: 'status' },
      { header: 'Created', controlName: 'createDate', displayPipe: 'date' },
      {
        header: 'Manage',
        controlName: 'action',
        actionColumnitems: [
          {
            type: 'icon',
            buttonAction: 'editUser',
            conditionalDisplay: {
              calculateDisplay: (status) => {
                return 'eye';
              },
              column: 'status',
            },
            hideOnColumnValue: { column: 'editUser', values: [false] },
          },
          {
            type: 'dropdown',
            buttonAction: 'action',
            hideOnColumnValue: { column: 'editUser', values: [true] },
            conditionalDisplay: { calculateDisplay: () => 'ellipsis', column: 'status' },
            dropdownItems: computed(() => {
              const items = [
                {
                  name: 'Resend Invite',
                  buttonAction: 'resendInvite',
                  conditionalDisabled: { calculateDisabled: (bool: boolean) => bool == false, column: 'resendInvite' },
                },
                {
                  name: 'Cancel Invite',
                  buttonAction: 'cancelInvite',
                  conditionalDisabled: { calculateDisabled: (bool: boolean) => bool == false, column: 'cancelInvite' },
                },
              ];
              return items;
            }),
          },
        ],
      },
    ],
    data: this.data,
    totalNumberOfItems: this.totalNumberOfItems,
    frontEndPagination: false,
    fetchData: false,
    displaySearch: false,
    useUtcDates: true,
  });

  constructor(private modalService: ModalService, private snackBar: MatSnackBar) {
    this.lookupStore.loadLookup('agencyRoles');
    this.lookupStore.loadLookup('agencyUserStatus');
    this.lookupStore.loadLookup('agencies');
    if (this.authStore.isAdmin()) {
      this.lookupStore.loadLookup('tenants');
    }
  }

  fetchAll(rawParams?: any) {
    const sortOrder = rawParams.sortOrder;
    const newSortOrder = this.sortField(sortOrder);
    const params = {
      ...rawParams,
      SortField: newSortOrder
        ? newSortOrder.active && newSortOrder.direction
          ? newSortOrder.active
          : this.defaultSortColumn
        : this.defaultSortColumn,
      SortDirection: newSortOrder ? newSortOrder.direction ?? this.defaultSortDirection : this.defaultSortDirection,
    };
    this.agencyUserGridStore.setFilterValues(params);
  }

  sortField(sort: Sort) {
    let activeSort: string = this.defaultSortColumn;
    let direction: string | null = this.defaultSortDirection;
    if (sort !== null && sort !== undefined) {
      activeSort = sort.active;
      switch (true) {
        case sort.direction === 'desc':
          direction = 'DESCENDING';
          break;
        case sort.direction === 'asc':
          direction = 'ASCENDING';
          break;
        case sort.direction === '':
          direction = null;
          break;
      }
    }
    return { active: activeSort, direction: direction };
  }

  actionEvent(event: GridEmitter) {
    if (event.action === 'fetchAll') {
      this.fetchAll(event.data);
    } else if (event.action === 'formChanges') {
      this.fetchAll(event.data);
    } else if (event.action === 'clearFilters') {
      this.fetchAll(event.data);
    } else if (event.action === 'onDestroy') {
      // on Destroy
    } else if (event.action === 'editUser') {
      this.editUser(event.data);
    } else if (event.action === 'resendInvite') {
      this.resendInvite(event.data);
    } else if (event.action === 'cancelInvite') {
      this.cancelInvite(event.data);
    }
  }

  resendInvite(data: any) {
    this.agencyStore
      .resendInvite(data.userInviteGuid)
      .then(() => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Success, message: 'Agency user invitation sent.' },
        });
      })
      .catch((error) => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 3000,
          data: { toastType: ToastType.Error, message: error.message },
        });
      });
    return;
  }

  cancelInvite(data: any) {
    const confirmData: ConfirmModalData = {
      confirmString: 'CANCEL',
      title: 'Are you sure?',
      subtitle: '',
      message: `Cancelling this invitation will deactivate the registration links in 
                previously sent to ${data.firstName} ${data.lastName} and remove the 
                history from your agency users grid.`,
      confirmButtonText: 'Cancel Invite',
    };
    this.modalService.open(
      ConfirmModalComponent,
      {
        modalType: ModalType.Small,
        data: confirmData,
      },
      (response: any) => {
        if (!response) {
          return;
        }
        this.agencyStore
          .cancelInvite(data.userInviteGuid ?? '')
          .then(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Success, message: 'Invite Cancelled' },
            });
            this.agencyUserGridStore.resource.reload();
          })
          .catch((error) => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Error, message: error.message },
            });
          });
      },
    );
  }

  editUser(data: any) {
    this.agencyStore.getAgent(data.id);
    const modalOptions = {
      modalType: ModalType.FullScreen,
      data: {
        agencyName: data.agencyName,
        createDate: data.createDate,
        roles: this.lookupStore.agencyRoles?.() ?? [],
      },
    };

    this.modalService.open(EditUserModalComponent, modalOptions, (agent) => {
      if (agent) {
        this.agencyStore
          .updateAgent(agent)
          .then(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: {
                toastType: ToastType.Success,
                title: 'User Updated',
                message: 'The agency user has been updated',
              },
            });
            this.agencyUserGridStore.resource.reload();
          })
          .catch((error) => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 5000,
              data: { toastType: ToastType.Error, message: error.message },
            });
          });
      }
    });
  }

  addAgencyUser() {
    const modalOptions = {
      modalType: ModalType.FullScreen,
      data: {
        agencyId: null,
      },
    };

    this.modalService.open(AddUsersModalComponent, modalOptions, (result) => {
      if (result) {
        this.agencyStore
          .inviteUsers(result)
          .then(() => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 3000,
              data: {
                toastType: ToastType.Success,
                title: 'Users Invited',
                message: 'The agency users have been sent an email invitation.',
              },
            });
          })
          .catch((error) => {
            this.snackBar.openFromComponent(SnackbarComponent, {
              duration: 3000,
              data: { toastType: ToastType.Error, title: 'Error Inviting Users', message: error.message },
            });
          })
          .finally(() => {
            this.agencyUserGridStore.resource.reload();
          });
      }
    });
  }
}
